import { useLocalization } from '@ais-ui/locale-management';
import { useToast } from '@ais-ui/ui-core';
import { useSaveSettings } from '@ais-ui/websockets';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useSettings } from '../SettingsProvider';
import { useSettingsPage } from '../SettingsPageProvider';

const messagesFormSchema = z.object({
  lookback_time_in_hours: z.number(),
  message_shape: z.string().optional(),
});

export type MessagesFormValues = z.infer<typeof messagesFormSchema>;

export const useMessagesForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { mutate: mutateSettings } = useSaveSettings();
  const { setUnsavedSettings } = useSettingsPage();

  const initialValues = {
    lookback_time_in_hours:
      settings['ribbonPlugin.settings'].msg_lookback_hours,
    message_shape: settings['ribbonPlugin.settings'].msg_area_of_interest,
  };

  const form = useForm<MessagesFormValues>({
    resolver: zodResolver(messagesFormSchema),
    defaultValues: initialValues,
  });

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  const onSubmit = (data: MessagesFormValues) => {
    setIsSubmitting(true);
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        msg_lookback_hours: data.lookback_time_in_hours,
        msg_area_of_interest: data.message_shape,
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-messages-enable-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsMessageSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-messages-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsMessageFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  };

  return {
    form,
    onSubmit,
    isSubmitting,
  };
};
